import { useEffect, useState } from 'react';
import { searchClients } from '@/lib/api';
import type { Client } from '@/lib/types';

interface Props {
  value: Client | null;
  onChange: (client: Client | null) => void;
  placeholder?: string;
  autoFocus?: boolean;
}

const inputCls =
  'w-full rounded-md border border-bmade-200 bg-white px-3 py-2 text-sm text-bmade-950 outline-none transition-colors focus:border-accent-500 dark:border-bmade-700 dark:bg-bmade-950/60 dark:text-white dark:focus:border-accent-400';

export function ClientPicker({ value, onChange, placeholder, autoFocus }: Props) {
  const [query, setQuery] = useState(value?.company_name ?? '');
  const [matches, setMatches] = useState<Client[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setQuery(value?.company_name ?? '');
  }, [value?.id]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      searchClients(query)
        .then(setMatches)
        .catch(() => setMatches([]));
    }, 200);
    return () => clearTimeout(t);
  }, [query, open]);

  return (
    <div className="relative">
      <input
        type="text"
        value={query}
        placeholder={placeholder ?? 'Zoek klant...'}
        autoFocus={autoFocus}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          if (value && e.target.value !== value.company_name) {
            onChange(null);
          }
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className={inputCls}
      />
      {open && matches.length > 0 && (
        <div className="absolute z-20 mt-1 max-h-48 w-full overflow-auto rounded-md border border-bmade-200 bg-white shadow-lg dark:border-bmade-800 dark:bg-bmade-950">
          {matches.slice(0, 10).map((c) => (
            <button
              type="button"
              key={c.id}
              onClick={() => {
                onChange(c);
                setQuery(c.company_name);
                setOpen(false);
              }}
              className={`block w-full px-3 py-1.5 text-left text-sm transition-colors ${
                value?.id === c.id
                  ? 'bg-accent-500/10 text-accent-700 dark:bg-accent-500/15 dark:text-accent-300'
                  : 'text-bmade-800 hover:bg-bmade-50 dark:text-bmade-200 dark:hover:bg-bmade-800'
              }`}
            >
              {c.company_name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
